import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Calculator, SealCheck, ArrowUpRight } from "@phosphor-icons/react";
import { formatPrice, LENDER } from "@/lib/constants";

function Row({ label, children }) {
  return (
    <div>
      <label className="lux-label">{label}</label>
      {children}
    </div>
  );
}

const inputCls =
  "w-full bg-[#0B0C10] border border-white/10 rounded-sm px-3 py-2.5 text-sm text-white outline-none focus:border-brand/50";

export default function MortgageCalculator({ price = 0 }) {
  const [downPct, setDownPct] = useState(20);
  const [rate, setRate] = useState(6.5);
  const [term, setTerm] = useState(30);
  const [taxPct, setTaxPct] = useState(1.2);
  const [insYr, setInsYr] = useState(1500);
  const [hoa, setHoa] = useState(0);

  const calc = useMemo(() => {
    const downAmt = (price * downPct) / 100;
    const loan = Math.max(price - downAmt, 0);
    const r = rate / 100 / 12;
    const n = term * 12;
    const pi = r > 0 ? (loan * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1) : loan / n;
    const taxMo = (price * (taxPct / 100)) / 12;
    const insMo = insYr / 12;
    const total = pi + taxMo + insMo + Number(hoa || 0);
    return { downAmt, loan, pi, taxMo, insMo, total: isFinite(total) ? total : 0 };
  }, [price, downPct, rate, term, taxPct, insYr, hoa]);

  const money = (n) => "$" + Math.round(n || 0).toLocaleString("en-US");

  return (
    <div className="bg-[#101116] border border-white/5 rounded-sm p-6" data-testid="mortgage-calculator">
      <h2 className="flex items-center gap-2 text-sm uppercase tracking-[0.18em] text-brand mb-5">
        <Calculator size={18} weight="duotone" /> Mortgage Estimator
      </h2>

      <div className="grid sm:grid-cols-3 gap-5 mb-6">
        <Row label={`Down payment (${downPct}%)`}>
          <input data-testid="mc-down" type="number" min="0" max="100" value={downPct} onChange={(e) => setDownPct(Number(e.target.value))} className={inputCls} />
          <p className="text-xs text-brand/90 mt-1.5" data-testid="mc-down-amount">= {money(calc.downAmt)} down</p>
        </Row>
        <Row label="Interest rate (%)">
          <input data-testid="mc-rate" type="number" step="0.1" min="0" value={rate} onChange={(e) => setRate(Number(e.target.value))} className={inputCls} />
        </Row>
        <Row label="Loan term (years)">
          <select data-testid="mc-term" value={term} onChange={(e) => setTerm(Number(e.target.value))} className={inputCls}>
            {[30, 20, 15, 10].map((t) => <option key={t} value={t}>{`${t} years`}</option>)}
          </select>
        </Row>
        <Row label="Property tax (%/yr)">
          <input data-testid="mc-tax" type="number" step="0.1" min="0" value={taxPct} onChange={(e) => setTaxPct(Number(e.target.value))} className={inputCls} />
        </Row>
        <Row label="Insurance ($/yr)">
          <input data-testid="mc-ins" type="number" min="0" value={insYr} onChange={(e) => setInsYr(Number(e.target.value))} className={inputCls} />
        </Row>
        <Row label="HOA ($/mo)">
          <input data-testid="mc-hoa" type="number" min="0" value={hoa} onChange={(e) => setHoa(Number(e.target.value))} className={inputCls} />
        </Row>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 pt-5 border-t border-white/5">
        <div>
          <p className="text-[11px] uppercase tracking-wider text-white/40 mb-1">Estimated monthly payment</p>
          <p className="text-4xl font-serif text-brand" data-testid="mc-total">{money(calc.total)}<span className="text-base text-white/40">/mo</span></p>
        </div>
        <div className="text-xs text-white/50 space-y-1 sm:text-right">
          <p>Principal &amp; interest: <span className="text-white/80">{money(calc.pi)}</span></p>
          <p>Taxes: <span className="text-white/80">{money(calc.taxMo)}</span> · Insurance: <span className="text-white/80">{money(calc.insMo)}</span></p>
          <p>Loan amount: <span className="text-white/80">{formatPrice(Math.round(calc.loan))}</span></p>
        </div>
      </div>
      <p className="text-[11px] text-white/30 mt-4">Estimate only. Actual rates, taxes &amp; insurance vary. Not a loan commitment.</p>

      <Link
        to="/financing"
        data-testid="get-preapproved-cta"
        className="group mt-5 flex items-center justify-between gap-4 rounded-sm border border-brand/30 bg-brand/[0.06] px-5 py-4 transition-colors hover:bg-brand/[0.12]"
      >
        <div className="flex items-center gap-3">
          <SealCheck size={28} weight="duotone" className="shrink-0 text-brand" />
          <div>
            <p className="text-sm font-medium text-white">Need financing?</p>
            <p className="text-xs text-white/50">{LENDER.name} · {LENDER.titleLong} · NMLS #{LENDER.nmls}</p>
          </div>
        </div>
        <span className="flex items-center gap-1.5 whitespace-nowrap rounded-sm bg-brand px-4 py-2 text-xs font-medium uppercase tracking-wider text-black transition-transform group-hover:translate-x-0.5">
          Meet Jeff <ArrowUpRight size={14} weight="bold" />
        </span>
      </Link>
    </div>
  );
}
